import { Injectable, InjectionToken, NgZone, inject, signal } from '@angular/core';
import { User } from '@angular/fire/auth';
import { ActivatedRoute } from '@angular/router';
import { filter, take, timeout } from 'rxjs';
import { hasScheduleGreeting, type ScheduleGreeting } from '@common/domain/schedule-greetings';
import { MASAccountModel } from 'src/app/models/mas-accounts.model';
import { MASScheduleExtrasModel, type ScheduleAttendance } from 'src/app/models/mas-schedules.model';
import {
	Firebase_Functions_Service,
	GoogleIdentityService,
	MAS_Accounts_Service,
	MAS_Schedules_Service,
} from 'src/app/services';
import { AdvancedDebugging } from 'src/app/utilities';

export type ScheduleDetailKind = 'parents' | 'yas';

/** Distinguishes Parents vs YAS detail behavior when sharing {@link ScheduleDetailFacade}. */
export const SCHEDULE_DETAIL_KIND = new InjectionToken<ScheduleDetailKind>('SCHEDULE_DETAIL_KIND');

export type ParentsRosterRow =
	| {
			kind: 'student';
			key: string;
			name: string;
			isTrial: boolean;
			classesAttended: number | null;
			endDateDisplay: string | null;
	  }
	| {
			kind: 'parent';
			key: string;
			parentId: string;
			name: string;
	  };

/**
 * Loads a single schedule by route `id` after auth, then applies Parents-specific or YAS-specific enrichment.
 * Scoped to {@link ParentsComponent} / {@link YASComponent} via `providers`.
 */
@Injectable()
export class ScheduleDetailFacade {
	private readonly advancedDebugging = new AdvancedDebugging();
	private readonly route = inject(ActivatedRoute);
	private readonly ngZone = inject(NgZone);
	private readonly googleIdentityService = inject(GoogleIdentityService);
	private readonly accountsService = inject(MAS_Accounts_Service);
	private readonly schedulesService = inject(MAS_Schedules_Service);
	private readonly firebaseFunctions = inject(Firebase_Functions_Service);
	private readonly kind = inject(SCHEDULE_DETAIL_KIND);

	readonly schedule = signal<MASScheduleExtrasModel | null>(null);
	readonly rosterRows = signal<ParentsRosterRow[]>([]);
	readonly loading = signal(false);
	readonly loadError = signal<string | null>(null);
	readonly greetingBusyParentIds = signal<ReadonlySet<string>>(new Set());
	/** Bumps when greetings mutate in place so checkbox bindings refresh. */
	readonly greetingsTick = signal(0);

	private currentUserId: string | null = null;

	start(): void {
		this.setLoadState({ loading: true, loadError: null, schedule: null, rosterRows: [] });
		this.route.paramMap.pipe(take(1)).subscribe({
			next: pm => {
				const id = pm.get('id');
				if (!id) {
					this.setLoadState({
						loading: false,
						loadError: 'Missing schedule id.',
						schedule: null,
						rosterRows: [],
					});
					return;
				}
				this.waitForUserThenLoad(id);
			},
			error: err => {
				this.setLoadState({
					loading: false,
					loadError: 'Could not read route.',
					schedule: null,
					rosterRows: [],
				});
				this.logError('dashboard:ScheduleDetailFacade.paramMap', err, { kind: this.kind });
			},
		});
	}

	isParentGreeted(parentAccountId: string | null | undefined): boolean {
		this.greetingsTick();
		if (!parentAccountId || !this.currentUserId) return false;
		const greetings = this.schedule()?.schedule.greetings ?? [];
		return hasScheduleGreeting(greetings, parentAccountId, this.currentUserId);
	}

	isGreetingBusy(parentAccountId: string | null | undefined): boolean {
		if (!parentAccountId) return false;
		return this.greetingBusyParentIds().has(parentAccountId);
	}

	async onGreetingToggle(parentAccountId: string, checked: boolean): Promise<void> {
		const schedule = this.schedule();
		if (!schedule?.id || !parentAccountId || !this.currentUserId) return;
		if (this.isGreetingBusy(parentAccountId)) return;

		const previous = [...(schedule.schedule.greetings ?? [])];
		const optimisticAt = new Date().toISOString();
		const optimistic: ScheduleGreeting[] = checked
			? hasScheduleGreeting(previous, parentAccountId, this.currentUserId)
				? previous
				: [...previous, { parentAccountId, userId: this.currentUserId, greetedAt: optimisticAt }]
			: previous.filter(
					row =>
						!(String(row.parentAccountId) === String(parentAccountId) && String(row.userId) === String(this.currentUserId)),
				);

		schedule.schedule.greetings = optimistic;
		this.greetingsTick.update(n => n + 1);
		this.setGreetingBusy(parentAccountId, true);

		try {
			const result = await this.firebaseFunctions.mutateScheduleGreetingAtomic({
				scheduleId: schedule.id,
				parentAccountId,
				mode: checked ? 'add' : 'remove',
				source: 'dashboard-parents',
			});
			schedule.schedule.greetings = result.greetings ?? [];
			this.greetingsTick.update(n => n + 1);
		} catch (error) {
			schedule.schedule.greetings = previous;
			this.greetingsTick.update(n => n + 1);
			this.logError('dashboard:ScheduleDetailFacade.onGreetingToggle', error, {
				scheduleId: schedule.id,
				parentAccountId,
				checked,
				kind: this.kind,
			});
		} finally {
			this.setGreetingBusy(parentAccountId, false);
		}
	}

	private waitForUserThenLoad(scheduleId: string): void {
		const current = this.googleIdentityService.auth.currentUser;
		if (current) {
			this.currentUserId = current.uid;
			void this.loadSchedule(scheduleId);
			return;
		}

		this.googleIdentityService.getUser
			.pipe(
				filter((user): user is User => !!user),
				take(1),
				timeout({ first: 15_000 }),
			)
			.subscribe({
				next: user => {
					this.currentUserId = user.uid;
					void this.loadSchedule(scheduleId);
				},
				error: err => {
					this.setLoadState({
						loading: false,
						loadError: 'Sign-in required.',
						schedule: null,
						rosterRows: [],
					});
					this.logError('dashboard:ScheduleDetailFacade.auth', err, {
						scheduleId,
						kind: this.kind,
					});
				},
			});
	}

	private async loadSchedule(scheduleId: string): Promise<void> {
		this.setLoadState({ loading: true, loadError: null, schedule: null, rosterRows: [] });
		try {
			const model = await this.schedulesService.getScheduleById(scheduleId);
			if (!model) {
				throw new Error(`Schedule with id "${scheduleId}" not found`);
			}
			if (this.kind === 'parents') {
				await this.attachAccountModelsToAttendance(model);
				if (model.attendance?.length) {
					model.attendance = [...model.attendance].sort((a, b) => (a.name || '').localeCompare(b.name || ''));
				}
				this.setLoadState({
					loading: false,
					loadError: null,
					schedule: model,
					rosterRows: this.buildRosterRows(model.attendance ?? []),
				});
				return;
			}
			await model.enrichAttendance(
				this.accountsService,
				['YAS'],
				(context, error) =>
					this.logError(`dashboard:ScheduleDetailFacade.enrichAttendance:${context}`, error, {
						scheduleId,
						kind: this.kind,
					}),
				'schoolThenName',
			);
			this.setLoadState({ loading: false, loadError: null, schedule: model, rosterRows: [] });
		} catch (error) {
			this.setLoadState({
				loading: false,
				loadError: error instanceof Error ? error.message : 'Failed to load schedule.',
				schedule: null,
				rosterRows: [],
			});
			this.logError('dashboard:ScheduleDetailFacade.loadSchedule', error, { scheduleId, kind: this.kind });
		}
	}

	private setLoadState(state: {
		loading: boolean;
		loadError: string | null;
		schedule: MASScheduleExtrasModel | null;
		rosterRows: ParentsRosterRow[];
	}): void {
		this.ngZone.run(() => {
			this.loading.set(state.loading);
			this.loadError.set(state.loadError);
			this.schedule.set(state.schedule);
			this.rosterRows.set(state.rosterRows);
		});
	}

	private buildRosterRows(attendance: ScheduleAttendance[]): ParentsRosterRow[] {
		const rows: ParentsRosterRow[] = [];
		for (const member of attendance) {
			const child = member.childAccountModel;
			const studentName = child?.pronunciation?.trim() || member.name || 'Student';
			const isTrial = !!child?.isTrial;
			rows.push({
				kind: 'student',
				key: `student-${member.id || studentName}`,
				name: studentName,
				isTrial,
				classesAttended: isTrial ? (member.classesAttended ?? 0) : null,
				endDateDisplay: isTrial ? (member.endDateDisplay ?? '-') : null,
			});

			for (const parent of member.parentAccountModels ?? []) {
				rows.push({
					kind: 'parent',
					key: `parent-${member.id}-${parent.id}`,
					parentId: parent.id,
					name: this.parentDisplayName(parent),
				});
			}
		}
		return rows;
	}

	private parentDisplayName(parent: MASAccountModel): string {
		return parent.pronunciation?.trim() || parent.fullName?.trim() || 'Parent';
	}

	private async attachAccountModelsToAttendance(schedule: MASScheduleExtrasModel): Promise<void> {
		const attendance = schedule.attendance;
		if (!attendance?.length) {
			return;
		}

		await Promise.all(
			attendance.map(async member => {
				if (!member.id || member.id === 'Free') {
					member.childAccountModel = null;
					member.parentAccountModel = null;
					member.parentAccountModels = [];
					member.classesAttended = null;
					member.endDateDisplay = null;
					return;
				}
				try {
					const childModel = await this.accountsService.getAccountModelById(member.id);
					member.childAccountModel = childModel;

					const linkedAccounts = await childModel.getLinkedAccounts();
					const parents = linkedAccounts.filter(account => account.isParent);
					member.parentAccountModels = parents.length ? parents : linkedAccounts;
					member.parentAccountModel = member.parentAccountModels[0] ?? null;

					if (childModel.isTrial) {
						member.classesAttended = await this.accountsService.getAccountAttendanceTotal(member.id);
						member.endDateDisplay = this.formatTrialEndDate(childModel);
					} else {
						member.classesAttended = null;
						member.endDateDisplay = null;
					}
				} catch (error) {
					member.childAccountModel = null;
					member.parentAccountModel = null;
					member.parentAccountModels = [];
					member.classesAttended = null;
					member.endDateDisplay = null;
					this.logError('dashboard:ScheduleDetailFacade.attachAccountModelsToAttendance', error, {
						memberId: member.id,
						scheduleId: schedule.id,
						kind: this.kind,
					});
				}
			}),
		);
	}

	private formatTrialEndDate(child: MASAccountModel): string {
		const date = child.trialEndDateAsDate;
		if (!date) return '-';
		return date.toLocaleString('en-US', { month: 'short', day: 'numeric' });
	}

	private setGreetingBusy(parentAccountId: string, busy: boolean): void {
		const next = new Set(this.greetingBusyParentIds());
		if (busy) next.add(parentAccountId);
		else next.delete(parentAccountId);
		this.greetingBusyParentIds.set(next);
	}

	private logError(operation: string, error: unknown, extra: Record<string, unknown>): void {
		this.advancedDebugging.captureError(operation, error, {
			tags: { operation },
			app: 'dashboard',
			...extra,
		});
	}
}
